import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Logo } from "@/components/Logo";
import { ArrowLeft } from "@phosphor-icons/react";

const CONTENT = {
  it: {
    title: "Informativa sulla privacy · Otelya",
    desc: "Come Otelya raccoglie, utilizza e protegge i dati personali di titolari di strutture ricettive e dei loro ospiti.",
    backHome: "Torna alla home",
    lastUpdated: "Ultimo aggiornamento: 21 ottobre 2026",
    intro: [
      "Otelya (\"noi\", \"la Piattaforma\") è una receptionist AI multilingua per strutture ricettive italiane. Trattiamo i dati personali dei nostri clienti (titolari di hotel, B&B, agriturismi, case vacanza) e degli ospiti che entrano in contatto con loro attraverso i nostri canali (email, WhatsApp, telefono, Messenger, Instagram Direct, form del sito).",
      "Titolare del trattamento: Alfio Moretti · Otelya · hello@otelya.app · Italia.",
    ],
    sections: [
      {
        h: "1. Quali dati raccogliamo",
        body: [
          "Dati account: nome, email, password (hashata), lingua, nome della struttura, città, tipologia (hotel/B&B/agriturismo).",
          "Dati di comunicazione: contenuto di email, messaggi WhatsApp, chiamate telefoniche, messaggi Messenger e Instagram Direct che gli ospiti inviano attraverso i canali collegati alla vostra struttura.",
          "Dati di pagamento: gestiti direttamente da Stripe (siamo un merchant Stripe). Non memorizziamo mai numeri di carta.",
          "Dati tecnici: indirizzo IP, tipo di browser, log applicativi, event log delle integrazioni.",
          "Statistiche di visita del sito otelya.app (strumento interno, nessun cookie e nessun servizio di terze parti): pagine viste, pagina di provenienza, parametri UTM delle campagne, città approssimativa e tipo di dispositivo, data e ora, associati a un identificativo casuale salvato nel browser. L'indirizzo IP non è conservato in chiaro (solo un hash irreversibile, usato per ricavare la città). Se arrivate cliccando un link presente in una nostra email, la visita viene associata al vostro contatto professionale.",
          "Dati Meta (Facebook/Instagram): quando collegate la vostra Pagina Facebook e il vostro account Instagram Professional, richiediamo tramite l'API Graph di Meta i permessi minimi necessari (pages_manage_posts, pages_manage_metadata, instagram_content_publish, instagram_manage_messages, pages_messaging) per: pubblicare post schedulati, leggere DM e commenti, rispondere in vostro nome.",
        ],
      },
      {
        h: "2. Come utilizziamo i dati",
        body: [
          "Erogare il servizio (rispondere automaticamente agli ospiti, pubblicare post, generare articoli blog).",
          "Migliorare la qualità delle risposte AI attraverso modelli linguistici (Anthropic Claude, Google Gemini) chiamati in modalità stateless — le vostre conversazioni non vengono usate per allenare modelli terzi.",
          "Fatturazione e gestione dell'abbonamento (Stripe).",
          "Comunicazioni operative (email di sistema, avvisi di sicurezza).",
          "Rispondere a richieste legali e prevenire abusi.",
        ],
      },
      {
        h: "3. Meta Platform Data (Facebook, Instagram, Messenger)",
        body: [
          "Otelya usa la Meta Graph API esclusivamente per pubblicare contenuti che voi ci autorizzate a pubblicare e per leggere/rispondere ai messaggi diretti che i vostri ospiti vi inviano.",
          "Non condividiamo mai dati Meta con terze parti a scopi pubblicitari.",
          "I dati di messaggistica Instagram/Messenger sono trattati solo per generare bozze di risposta AI e permettervi di rispondere dal Dashboard.",
          "Potete revocare l'accesso in qualsiasi momento dalle impostazioni del vostro Business Manager di Meta oppure disconnettendo l'integrazione dalla dashboard Otelya. Alla disconnessione, cancelliamo entro 30 giorni tutti i token e i messaggi archiviati.",
        ],
      },
      {
        h: "4. Condivisione con terze parti",
        body: [
          "Anthropic (Claude) e Google (Gemini): elaborazione AI del testo, in modalità stateless.",
          "ElevenLabs: sintesi vocale, solo quando attivate la voice AI.",
          "Twilio: chiamate telefoniche, solo quando attivate il numero AI.",
          "Resend: invio email transazionali e outreach.",
          "Stripe: pagamenti e gestione abbonamenti.",
          "Meta Platforms: pubblicazione e messaggistica social.",
          "MongoDB Atlas: database ospitato in UE (Francoforte).",
        ],
      },
      {
        h: "5. Base giuridica e conservazione",
        body: [
          "Trattiamo i dati sulla base del contratto (art. 6.1.b GDPR) per erogare il servizio, e del legittimo interesse per prevenire frodi e migliorare il servizio.",
          "Conserviamo i dati account per la durata dell'abbonamento + 24 mesi per obblighi fiscali.",
          "Statistiche di visita: trattate sulla base del legittimo interesse (art. 6.1.f GDPR) per migliorare il sito e, per i contatti professionali già raggiunti via email, per inviare al massimo un messaggio di follow-up ogni 30 giorni, sempre con possibilità di opposizione rispondendo 'rimuovi' (art. 21). Vengono cancellate automaticamente dopo 13 mesi. Potete bloccarle in qualsiasi momento cancellando i dati del sito dal browser.",
          "I messaggi degli ospiti sono conservati per 12 mesi rolling.",
          "Alla cancellazione dell'account, tutti i dati vengono eliminati entro 30 giorni salvo obblighi di legge.",
        ],
      },
      {
        h: "6. I vostri diritti (GDPR)",
        body: [
          "Diritto di accesso, rettifica, cancellazione, portabilità, opposizione, limitazione del trattamento.",
          "Per esercitarli, scriveteci a hello@otelya.app · rispondiamo entro 30 giorni.",
          "Diritto di reclamo al Garante Privacy italiano (www.garanteprivacy.it).",
        ],
      },
      {
        h: "7. Sicurezza",
        body: [
          "Tutte le comunicazioni tra il vostro browser e Otelya sono cifrate TLS 1.3.",
          "Le password sono hashate con bcrypt.",
          "Gli access token OAuth (Google, Meta) sono cifrati at-rest.",
          "Nessun dipendente Otelya legge il contenuto dei vostri messaggi ospiti se non su vostra esplicita richiesta di supporto.",
        ],
      },
      {
        h: "8. Cancellazione dati",
        body: [
          "Per cancellare il vostro account e tutti i dati associati (inclusi i dati ottenuti da Meta Platforms), inviate una richiesta a hello@otelya.app con oggetto \"Cancellazione account\". Cancellazione completata entro 30 giorni.",
        ],
      },
      {
        h: "9. Modifiche a questa informativa",
        body: [
          "Aggiorneremo questa pagina in caso di modifiche materiali e vi avviseremo via email almeno 15 giorni prima dell'entrata in vigore.",
        ],
      },
      {
        h: "10. Contatti",
        body: [
          "Alfio Moretti · Otelya · hello@otelya.app",
        ],
      },
    ],
  },
  en: {
    title: "Privacy Policy · Otelya",
    desc: "How Otelya collects, uses and protects personal data of hospitality property owners and their guests.",
    backHome: "Back to home",
    lastUpdated: "Last updated: October 21, 2026",
    intro: [
      "Otelya (\"we\", \"the Platform\") is a multilingual AI receptionist for Italian hospitality businesses. We process personal data of our customers (owners of hotels, B&Bs, agriturismi, vacation rentals) and of the guests who contact them through the channels we integrate (email, WhatsApp, phone, Messenger, Instagram Direct, website forms).",
      "Data controller: Alfio Moretti · Otelya · hello@otelya.app · Italy.",
    ],
    sections: [
      {
        h: "1. What data we collect",
        body: [
          "Account data: name, email, hashed password, language, property name, city, property type.",
          "Communication data: content of emails, WhatsApp messages, phone calls, Messenger and Instagram Direct messages guests send through channels connected to your property.",
          "Payment data: handled directly by Stripe. We never store card numbers.",
          "Technical data: IP address, browser type, application logs, integration event logs.",
          "Website visit statistics for otelya.app (in-house tool, no cookies, no third-party services): pages viewed, referring page, campaign UTM parameters, approximate city and device type, date and time, linked to a random identifier stored in your browser. The IP address is never stored in clear text (only an irreversible hash, used to derive the city). If you arrive by clicking a link in one of our emails, the visit is linked to your business contact.",
          "Meta data (Facebook/Instagram): when you connect your Facebook Page and Instagram Professional account, we request via Meta Graph API the minimum required scopes (pages_manage_posts, pages_manage_metadata, instagram_content_publish, instagram_manage_messages, pages_messaging) to publish scheduled posts, read DMs and comments, and reply on your behalf.",
        ],
      },
      {
        h: "2. How we use the data",
        body: [
          "Deliver the service (auto-reply to guests, publish posts, generate blog articles).",
          "Improve AI quality through language models (Anthropic Claude, Google Gemini) called in stateless mode — your conversations are not used to train third-party models.",
          "Billing and subscription management (Stripe).",
          "Operational communications (system emails, security alerts).",
          "Respond to legal requests and prevent abuse.",
        ],
      },
      {
        h: "3. Meta Platform Data (Facebook, Instagram, Messenger)",
        body: [
          "Otelya uses the Meta Graph API solely to publish content you authorize us to publish and to read/reply to direct messages your guests send you.",
          "We never share Meta data with third parties for advertising purposes.",
          "Instagram/Messenger messaging data is processed only to generate AI draft replies and let you respond from the Dashboard.",
          "You can revoke access at any time in your Meta Business Manager settings or by disconnecting the integration in the Otelya dashboard. On disconnect, we delete all tokens and stored messages within 30 days.",
        ],
      },
      {
        h: "4. Third-party sharing",
        body: [
          "Anthropic (Claude) and Google (Gemini): AI text processing, stateless.",
          "ElevenLabs: text-to-speech, only when you enable AI voice.",
          "Twilio: phone calls, only when you enable AI number.",
          "Resend: transactional and outreach emails.",
          "Stripe: payments and subscription management.",
          "Meta Platforms: social publishing and messaging.",
          "MongoDB Atlas: EU-hosted database (Frankfurt).",
        ],
      },
      {
        h: "5. Legal basis and retention",
        body: [
          "We process data under contract (Art. 6.1.b GDPR) to deliver the service, and under legitimate interest to prevent fraud and improve the service.",
          "We retain account data for subscription duration + 24 months for tax obligations.",
          "Visit statistics: processed under legitimate interest (Art. 6.1.f GDPR) to improve the website and, for business contacts already reached by email, to send at most one follow-up message every 30 days, always with the right to object by replying 'remove' (Art. 21). They are deleted automatically after 13 months. You can stop them at any time by clearing this site's data in your browser.",
          "Guest messages are retained for 12 rolling months.",
          "On account deletion, all data is erased within 30 days unless legal obligations require otherwise.",
        ],
      },
      {
        h: "6. Your rights (GDPR)",
        body: [
          "Right of access, rectification, erasure, portability, objection, restriction.",
          "To exercise them, write to hello@otelya.app · we reply within 30 days.",
          "Right to complain to the Italian Garante Privacy (www.garanteprivacy.it).",
        ],
      },
      {
        h: "7. Security",
        body: [
          "All communications between your browser and Otelya are TLS 1.3 encrypted.",
          "Passwords are hashed with bcrypt.",
          "OAuth access tokens (Google, Meta) are encrypted at rest.",
          "No Otelya employee reads the content of your guest messages unless you explicitly request support.",
        ],
      },
      {
        h: "8. Data deletion",
        body: [
          "To delete your account and all associated data (including data obtained from Meta Platforms), send a request to hello@otelya.app with subject \"Account deletion\". Deletion completed within 30 days.",
        ],
      },
      {
        h: "9. Changes to this policy",
        body: [
          "We'll update this page for material changes and notify you by email at least 15 days before they take effect.",
        ],
      },
      {
        h: "10. Contact",
        body: [
          "Alfio Moretti · Otelya · hello@otelya.app",
        ],
      },
    ],
  },
};

function useLang() {
  const q = new URLSearchParams(useLocation().search).get("lang")?.toLowerCase();
  if (q === "en" || q === "it") return q;
  return (typeof navigator !== "undefined" && (navigator.language || "it").toLowerCase().startsWith("en")) ? "en" : "it";
}

export default function Privacy() {
  const lang = useLang();
  const c = CONTENT[lang] || CONTENT.it;
  return (
    <div className="min-h-screen bg-canvas text-ink" data-testid="privacy-page">
      <Helmet>
        <html lang={lang} />
        <title>{c.title}</title>
        <meta name="description" content={c.desc} />
        <link rel="canonical" href="https://otelya.app/privacy" />
        <link rel="alternate" hrefLang="it-IT" href="https://otelya.app/privacy" />
        <link rel="alternate" hrefLang="en-US" href="https://otelya.app/privacy?lang=en" />
        <meta property="og:title" content={c.title} />
        <meta property="og:description" content={c.desc} />
        <meta property="og:url" content="https://otelya.app/privacy" />
      </Helmet>

      <nav className="border-b border-line/40">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link to="/" data-testid="privacy-back-home">
            <Logo />
          </Link>
          <Link to={`/?lang=${lang}`} className="text-sm text-stone hover:text-terracotta transition-colors flex items-center gap-1">
            <ArrowLeft size={14} /> {c.backHome}
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight text-ink">
          {c.title.split(" · ")[0]}
        </h1>
        <p className="mt-3 text-sm text-stone italic">{c.lastUpdated}</p>

        <div className="mt-10 space-y-5">
          {c.intro.map((p, i) => (
            <p key={i} className="text-base sm:text-lg text-ink leading-[1.75]">{p}</p>
          ))}
        </div>

        <div className="mt-10 space-y-12">
          {c.sections.map((s, i) => (
            <section key={i} data-testid={`privacy-section-${i}`}>
              <h2 className="font-serif text-2xl lg:text-3xl mb-4 text-ink">{s.h}</h2>
              <ul className="space-y-3 list-disc pl-6">
                {s.body.map((b, j) => (
                  <li key={j} className="text-base text-ink leading-[1.75]">{b}</li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-12 border-t border-line/60 text-sm text-stone">
          <Link to="/terms" className="underline hover:text-terracotta">
            {lang === "en" ? "Terms of Service" : "Condizioni d'uso"}
          </Link>
          {" · "}
          <a href="mailto:hello@otelya.app" className="hover:text-terracotta">hello@otelya.app</a>
        </div>
      </article>
    </div>
  );
}
